<template>
  <form class="needs-validation ml-0" @submit.prevent="update" novalidate>
    <div class="col px-0 pr-lg-3">
      <section class="bg-white rounded mb-4 p-3 p-sm-4">
        <div v-if="!isLoading">
          <h1 class="h2 mb-3">{{ committee.id ? 'Committee Profile' : 'New Committee' }}</h1>
          <div role="alert" class="alert alert-warning" v-if="errors" ref="errorAlert">{{ errors }}</div>

          <div>
            <div class="row">
              <div class="col-12 form-group">
                <label for="notes">Notes</label>
                <textarea class="form-control" id="notes" v-model="$v.committee.notes.$model"></textarea>
              </div>
              <div class="col-12 col-md-6 form-group">
                <label for="committeeName">Committee Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" :class="{ 'is-invalid': $v.committee.name.$error || errorFields.name}"
                       id="committeeName"
                       required
                       v-model.trim="$v.committee.name.$model">
                <div class="invalid-tooltip">Committee name must contain letters and spaces only.</div>
              </div>

              <div class="col-6 col-md-3 form-group">
                <label for="e-zone">Zone <span class="text-danger">*</span></label>
                <select
                  class="form-control"
                  :class="{ 'is-invalid': $v.committee.zone_id.$error }"
                  id="e-zone"
                  required
                  v-model.trim="$v.committee.zone_id.$model"
                >
                  <option :value="null">Unspecified</option>
                  <option v-for="(zone, index) in $store.state.listZones.filter(zone => !zone.deleted_at)" :key="index"
                          :value="zone.id">{{ zone.code }}
                  </option>
                </select>
                <div class="invalid-tooltip">Please include your Zone.</div>
                <div class="alert alert-warning alert-sm" v-if="zoneNotFound && !zoneNotSpecified">Unable to find matching
                  Zone.
                </div>
                <div class="alert alert-warning alert-sm zone-alert" v-else-if="zoneNotSpecified">Please refine the postcode to find zone matches.</div>
                <div class="alert alert-success alert-sm zone-alert" v-else-if="zoneFound">Found matching Zone.</div>
              </div>

              <div class="col-12 col-md-6 form-group">
                <label for="contactName">Contact Name <span class="text-danger">*</span></label>
                <input type="text"
                  class="form-control"
                  :class="{ 'is-invalid' : $v.committee.contact_name.$error || errorFields.contact_name }"
                  id="contactName"
                  required
                  v-model.trim="$v.committee.contact_name.$model">
                <div class="invalid-tooltip">
                  Please include the valid contact name.
                </div>
              </div>

              <div v-if="committee && committee.id" class="col-12 col-md-6 form-group">
                <label for="chequesTo">Cheques To <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="chequesTo"
                  v-model.trim="$v.committee.cheques_to.$model"
                  :class="{ 'is-invalid' : $v.committee.cheques_to.$error }"
                >
                <div class="invalid-tooltip">
                  Please include the valid cheques to.
                </div>
              </div>

              <div class="col-12 col-md-6 form-group">
                <label for="committeeLogoFile">Committee logo</label>
                <div class="custom-file">
                  <input type="file"
                         class="form-control custom-file-input"
                         :name ="logo_file"
                         :class="{ 'is-invalid': logoError.fileSize }"
                         id="committeeLogoFile" accept="image/*" @change="logoUpload">
                  <label class="custom-file-label font-weight-normal"
                         for="committeeLogoFile">{{ logo_name ? logo_name : 'Choose file...' }}</label>
                  <small>jpg, jpeg, png, tiff, gif file type required.</small>
                   <div class="invalid-tooltip" v-if="logoError.fileSize">Logo File may not be greater than 250 kilobytes</div>
                  <button
                    v-if="logo_name"
                    type="button"
                    class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1"
                    @click="deleteLogoAttachment">
                    <span class="sr-only">Delete File</span>
                    <div class="btn-remove">
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                        <g fill="currentColor">
                          <path
                            d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/>
                          <path
                            d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/>
                          <path
                            d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/>
                        </g>
                      </svg>
                    </div>
                  </button>
                </div>
                <div class="attached-file-wrapper" v-if="this.committee.logo_file_path">
                  {{ this.committee.logo_file_name }}
                  <div class="btn-remove-file" @click="deleteExistsAttachment">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                      <g fill="currentColor">
                        <path
                          d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path>
                      </g>
                    </svg>
                  </div>
                  <div class="btn-download-file" @click="downloadLogoFile(committee.id)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                      <g fill="currentColor">
                        <path
                          d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"></path>
                        <path
                          d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"></path>
                      </g>
                    </svg>
                  </div>
                </div>
              </div>

              <div class="col-12 col-md-6 form-group">
                <label for="picNumber">PIC number</label>
                <input type="text" class="form-control" id="picNumber" v-model.trim="$v.committee.pic_number.$model">
              </div>

              <div class="col-12 col-md-6 form-group">
                <label for="contactEmail">Contact Email <span class="text-danger">*</span></label>
                <input type="text" class="form-control" id="contactEmail"
                       :class="{ 'is-invalid': $v.committee.contact_email.$error || errorFields.contact_email}"
                       @blur="$v.committee.contact_email.$touch()" required
                       v-model.lazy="$v.committee.contact_email.$model">
                <div class="invalid-tooltip">Please include the valid Email.</div>
              </div>

              <div class="col-12 col-md-6 form-group">
                <label for="committeePhone">Contact Phone <span class="text-danger">*</span></label>
                <input type="text" class="form-control" id="committeePhone"
                       :class="{ 'is-invalid':  $v.committee.contact_phone.$error || errorFields.contact_phone }"
                       required
                       v-model.trim="$v.committee.contact_phone.$model">
                <div class="invalid-tooltip" v-if="!$v.committee.contact_phone.required">Please include the Phone number.</div>
                <div class="invalid-tooltip" v-if="!$v.committee.contact_phone.phoneRegex">Please include the valid Phone number.</div>
              </div>

              <div class="col-12 col-md-6 mb-4 form-group" v-if="!committee.id">
                <label for="c-isFinancial">Status</label>
                <select class="form-control" id="c-isFinancial" required v-model="committee.is_financial">
                  <option :value="0">Unfinancial</option>
                  <option :value="1">Financial</option>
                </select>
                <div class="invalid-tooltip">Please include the Status.</div>
              </div>

              <div v-if="committee && committee.id" class="d-flex flex-wrap">
                <div class="col-12 col-md-6 form-group">
                  <label for="committeeAbn">ABN <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" id="committeeAbn" :class="{ 'is-invalid': $v.committee.abn.$error }"
                         v-model.trim="$v.committee.abn.$model">
                  <div class="invalid-tooltip">Please include the ABN.</div>
                </div>

                <div class="col-12 col-md-6 form-group">
                  <label for="committeeAddress">Address <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" id="committeeAddress"
                         :class="{ 'is-invalid': $v.committee.address.$error || errorFields.line1}"
                         v-model.trim="$v.committee.address.$model">
                  <div class="invalid-tooltip">Please include the valid address.</div>
                </div>

                <div class="col-12 col-md-6 form-group">
                  <label for="committeeCity">City <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" id="committeeCity"
                         :class="{'is-invalid': $v.committee.city.$error || errorFields.city }"
                         @input="findZone()"
                         v-model.trim="$v.committee.city.$model">
                  <div class="invalid-tooltip">Please include the valid city.</div>
                </div>

                <div class="col-12 col-md-6 form-group">
                  <label for="state">State <span class="text-danger">*</span></label>
                  <select class="form-control" id="state" :class="{ 'is-invalid': $v.committee.state.$error }"
                          v-model.trim="$v.committee.state.$model">
                    <option disabled></option>
                    <option value="QLD">Queensland</option>
                    <option value="NSW">New South Wales</option>
                    <option value="VIC">Victoria</option>
                    <option value="ACT">Australian Capital Territory</option>
                    <option value="SA">South Australia</option>
                    <option value="NT">Northern Territory</option>
                    <option value="WA">Western Australia</option>
                    <option value="TAS">Tasmania</option>
                  </select>
                  <div class="invalid-tooltip">Please include your State.</div>
                </div>

                <div class="col-6 col-md-3 form-group">
                  <label for="postcode">Postcode <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" id="postcode" maxlength="4" pattern="\d{4}"
                         @input="findZone()" :class="{ 'is-invalid': $v.committee.post_code.$error || errorFields.postal_code}"
                         v-model.trim="$v.committee.post_code.$model">
                  <div class="invalid-tooltip">Please include the valid postcode.</div>
                </div>

                <div class="col-6 col-md-3 form-group">
                  <label for="committeeBankAbbreviation">Bank Abbreviation</label>
                  <input type="text" class="form-control" :class="{ 'is-invalid': $v.committee.bank_abbreviation.$error }"
                         id="committeeBankAbbreviation" v-model.trim="$v.committee.bank_abbreviation.$model">
                  <div class="invalid-tooltip">Please include the Bank Abbreviation.</div>
                  <small class="small-notes small-notes-per">The bank abbreviation must be approved Financial Institution
                    abbreviation.</small>
                </div>

                <div class="col-6 col-md-3 form-group">
                  <label for="committeeBSB">BSB <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" :class="{ 'is-invalid': $v.committee.bsb.$error || errorFields.routing_number}" id="committeeBSB"
                         v-model.trim="$v.committee.bsb.$model" maxlength="7"
                         @input="validateBankBsb"
                  >
                  <div class="invalid-tooltip" v-if="!$v.committee.bsb.bsbRegex || $v.committee.bsb.$error || errorFields.routing_number">Please include the valid BSB.</div>
                  <small class="small-notes small-notes-per">Please enter the bank's BSB in the format XXX-YYY.</small>
                </div>

                <div class="col-6 col-md-3 form-group">
                  <label for="committeeName">Account Number <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" :class="{ 'is-invalid': $v.committee.account_number.$error || errorFields.account_number}"
                         id="committeeAccountNumber"
                         v-model.trim="$v.committee.account_number.$model">
                  <div class="invalid-tooltip">Please include correct the Account Number.</div>
                </div>

                <div class="col-12 col-md-6 mb-4 form-group">
                  <label>Status</label>
                  <select class="form-control" id="isFinancial" v-model="committee.is_financial">
                    <option :value="0">Unfinancial</option>
                    <option :value="1">Financial</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-if="committee && committee.id">
              <div class="row">
                <div class="col-12 col-md-6 form-group">
                  <div><label>Credit Card Payments</label></div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input class="custom-control-input" type="radio" id="accept" value="1"
                           :checked="committee.is_enabled_stripe == '1'" v-model="committee.is_enabled_stripe">
                    <label class="custom-control-label font-weight-normal" for="accept">Accept</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input class="custom-control-input" type="radio" id="notAccept" value="0"
                           :checked="committee.is_enabled_stripe == '0'" v-model="committee.is_enabled_stripe">

                    <label class="custom-control-label font-weight-normal" for="notAccept">Not Accept</label>
                  </div>
                  <br>
                  <small class="small-notes small-notes-per">To accept instant online payments such as credit card for nomination fees.</small>
                </div>
              </div>

              <div v-if="committee.is_enabled_stripe == '1'">
                <div class="row">
                  <div class="col-6 col-md-3 form-group">
                    <label>Bank Owner First Name </label>
                    <input type="text" class="form-control" required
                           :class="{ 'is-invalid': $v.committee.bank_owner_first_name.$error }"
                           v-model.trim="$v.committee.bank_owner_first_name.$model">
                    <div class="invalid-tooltip">Please include the Bank Owner First Name.</div>
                  </div>
                  <div class="col-6 col-md-3 form-group">
                    <label>Bank Owner Last Name </label>
                    <input type="text" class="form-control" required
                           :class="{ 'is-invalid': $v.committee.bank_owner_last_name.$error }"
                           v-model.trim="$v.committee.bank_owner_last_name.$model">
                    <div class="invalid-tooltip">Please include the Bank Owner Last Name.</div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-6 col-md-3 form-group">
                    <label for="bankOwnerDOB">Bank Owner Day Of Birth </label>
                    <input type="date" class="form-control form-date" id="bankOwnerDOB" required
                           :class="{ 'is-invalid': $v.committee.bank_owner_dob.$error }"
                           v-model.trim="$v.committee.bank_owner_dob.$model" :placeholder="'dd.mm.yyyy'"
                           pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
                    <div class="invalid-tooltip">Please include the Bank Owner Day Of Birth.</div>
                  </div>
                  <div class="col-6 col-md-3 form-group" v-if="!committee.is_stripe_verified">
                    <label for="bankIdentityFile">Bank Identity File</label>
                    <div class="custom-file">
                      <input type="file"
                             class="form-control custom-file-input"
                             :class="{ 'is-invalid': fileError.required || fileError.fileSize }"
                             id="bankIdentityFile" accept="image/*, application/pdf" @change="fileUpload">
                      <label class="custom-file-label font-weight-normal"
                             for="bankIdentityFile">{{ file_name ? file_name : 'Choose file...' }}</label>
                      <div class="invalid-tooltip" v-if="fileError.required">Please include the Bank Identity File.</div>
                      <div class="invalid-tooltip" v-if="fileError.fileSize">Bank Identity File may not be greater than 1 mb</div>
                      <small>File needs to include BOTH sides of your identification in colour</small>
                      <button
                        v-if="file_name"
                        type="button"
                        class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1"
                        @click="deleteAttachment">
                        <span class="sr-only">Delete File</span>
                        <div class="btn-remove">
                          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                            <g fill="currentColor">
                              <path
                                d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/>
                              <path
                                d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/>
                              <path
                                d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/>
                            </g>
                          </svg>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-6 col-md-3 form-group gst_registered">
                  <div class="custom-control custom-checkbox">
                    <input type="checkbox" class="custom-control-input" id="gst_registered"
                           v-model="committee.gst_registered">
                    <label class="custom-control-label" for="gst_registered">
                      I am GST registered
                    </label>
                    <div class="invalid-tooltip">Please include true or false.</div>
                  </div>
                </div>
              </div>

              <div class="col-12 d-flex d-lg-none justify-content-end">
                <button :class="['btn btn-block-mob', processing ? 'btn-primary-dark' : 'btn-primary']"
                        @click="handleSubmit">
                  {{ !processing ? 'Update' : '' }}
                  <div v-if="processing" class="spinner-border text-light" role="status"><span
                    class="sr-only">Loading...</span></div>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="text-center p-2" v-if="isLoading">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
      </section>
    </div>

    <desktop-action-bar>
      <button :class="['btn btn-block', processing ? 'btn-primary-dark' : 'btn-primary']" @click="handleSubmit">
        {{ !processing ? 'Save' : '' }}
        <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span>
        </div>
      </button>
    </desktop-action-bar>
  </form>
</template>

<script>
import axios from 'axios'
import HANDLESUBMIT from '@/mixins/formValidation.js'
import { email, helpers, required } from "vuelidate/lib/validators"
import Vue from "vue"
import Vuelidate from 'vuelidate'
import DesktopActionBar from '@/components/DesktopActionBar'

const bsbRegex = helpers.regex('bsbRegex', /^[[\d]{3}[-]{1}[\d]{3}$/)
const phoneRegex = helpers.regex('phoneRegex', /^([+][7][0-9]{10})|([+][6][1][0-9]{1,10})$/)
const abnRegex = helpers.regex('abnRegex', /^[\d]{11}$/)
const nameRegex = helpers.regex('name', /^[A-Za-z\s]*$/)

Vue.use(Vuelidate)

const isValidLogoSize = (value) => {
  if (!value) {
    return true
  }
  return (value.size < 512 * 512)
}

export default {
  name: 'CommitteeProfileDetails',
  props: ['committeeDetails', 'isLoading'],
  mixins: [HANDLESUBMIT],
  components: {
    DesktopActionBar
  },
  data() {
    return {
      committee: { ...this.committeeDetails },
      user: localStorage.role,
      processing: false,
      zoneNotFound: false,
      zoneFound: false,
      zoneNotSpecified: false,
      zoneLocations: [],
      errors: '',
      errorStatus: '',
      file_name: null,
      logo_name: null,
      files: [],
      logo_file: [],
      fileError: { fileSize: false, required: false },
      logoError: { logoSize: false, required: false },
      errorFields: {}
    }
  },
  validations() {
    const validationRules = {
      committee: {
        state: {},
        post_code: {},
        zone_id: {
          required
        },
        name: {
          required,
          nameRegex
        },
        contact_email: {
          required, email
        },
        contact_phone: {
          required,
          phoneRegex
        },
        pic_number: {},
        abn: {
          abnRegex
        },
        cheques_to: {},
        address: {},
        city: {},
        account_number: {},
        bsb: {
          bsbRegex
        },
        contact_name: {
          required
        },
        bank_abbreviation: {},
        bank_owner_first_name: {},
        bank_owner_last_name: {},
        bank_owner_dob: {},
        notes: {}
      }
    }
    if (Number(this.committee.is_enabled_stripe)) {
      validationRules.committee.bank_owner_first_name = { required }
      validationRules.committee.bank_owner_last_name = { required }
      validationRules.committee.bank_owner_dob = { required }
      validationRules.committee.state = { required }
      validationRules.committee.post_code = { required }
      validationRules.committee.abn = { required, abnRegex }
      validationRules.committee.address = { required }
      validationRules.committee.city = { required }
      validationRules.committee.account_number = { required }
      validationRules.committee.bsb = { required, bsbRegex }
    }
    if (this.committee.id) {
      validationRules.committee.cheques_to = { required }
    } else {
      validationRules.committee.is_financial = { required }
    }
    return validationRules
  },
  mounted() {
    if (this.committee.id) {
      axios.get(`${this.$api}/admin/committee-details/${this.committee.id}`)
        .then((response) => {
          // eslint-disable-next-line
          this.committee = response.data.data
        })
        .catch((error) => {
          // eslint-disable-next-line
          console.log(error)
        })
    }
    this.setDefaultValues()
  },
  created() {
    this.getZoneLocations()
  },
  watch: {
    committeeDetails() {
      this.committee = this.committeeDetails
    }
  },
  methods: {
    scrollToError() {
      this.$nextTick(() => {
        if (this.$refs.errorAlert) {
          this.$refs.errorAlert.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    updateCommittee() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.submitStatus = 'ERROR'
        return
      }

      this.processing = true
      this.data = {
        id: this.committee.id,
        zone_id: this.committee.zone_id,
        name: this.committee.name,
        contact_name: this.committee.contact_name,
        contact_email: this.committee.contact_email,
        contact_phone: this.committee.contact_phone,
        is_financial: this.committee.is_financial,
        gst_registered: this.committee.gst_registered,
        cheques_to: this.committee.cheques_to,
        pic_number: this.committee.pic_number,
        account_number: this.committee.account_number,
        bsb: this.committee.bsb,
        post_code: this.committee.post_code,
        state: this.committee.state,
        city: this.committee.city,
        abn: this.committee.abn,
        is_enabled_stripe: this.committee.is_enabled_stripe,
        address: this.committee.address,
        bank_owner_first_name: this.committee.bank_owner_first_name,
        bank_owner_last_name: this.committee.bank_owner_last_name,
        bank_owner_dob: this.committee.bank_owner_dob,
        bank_abbreviation: this.committee.bank_abbreviation,
        is_stripe_verified: this.committee.is_stripe_verified,
        file_name: null,
        logo_name: null,
        file: [],
        logo_file: [],
        fileError: { fileSize: false, required: false },
        logoError: { logoSize: false, required: false },
        notes: this.committee.notes
      }

      this.submitStatus = 'PENDING'
      const formData = new FormData()
      formData.append('identity_file', this.file)
      formData.append('logo_file', this.logo_file)
      formData.append('fields', JSON.stringify(this.data))

      // eslint-disable-next-line
      axios.post(`${this.$api}/admin/committee-edit`, formData)
        .then((response) => {
          this.processing = false
          // eslint-disable-next-line
          if (response.data.status === 'success') {
            // eslint-disable-next-line
            this.submitStatus = 'SUCCESS'
            this.$router.push({ name: 'Committees', params: { success: 'Committee saved successfully' } })
          } else {
            this.fieldsValidation(response)
            if (response.data.errors.contact_email) {
              // Contact email
              this.success = ''
              this.errorStatus = 'email'
              if (response?.data?.errors?.contact_email[0]) {
                this.errors = response.data.errors.contact_email[0]
              } else {
                this.errors = 'Please include correct email address.'
              }
            }
            if (response.data.status === 'validation_error') {
              if (response.data.errors.contact_email !== undefined) {
                this.errors = 'Committee email must be unique among committee and user emails'
                this.errorFields.contact_email = true
              } else if (response.data.errors.account_number !== undefined) {
                this.errors = 'Committee account number is not valid'
                this.errorFields.account_number = true
              } else if (response.data.errors.routing_number !== undefined) {
                this.errors = 'Committee BSB is not valid'
                this.errorFields.routing_number = true
              } else if (response.data.errors.postal_code !== undefined) {
                this.errors = 'Committee postcode is not valid'
                this.errorFields.postal_code = true
              } else if (response.data.errors.city !== undefined) {
                this.errors = 'Committee city is not valid'
                this.errorFields.city = true
              } else if (response.data.errors.line1 !== undefined) {
                this.errors = 'Committee address file is not valid'
                this.errorFields.line1 = true
              } else if (response.data.errors.phone !== undefined) {
                this.errors = 'Contact Phone is not valid'
                this.errorFields.phone = true
              } else if (response.data.errors.contact_phone !== undefined) {
                this.errors = 'Contact Phone is not valid'
                this.errorFields.contact_phone = true
              } else if (response.data.errors.abn !== undefined) {
                this.errorStatus = 'Committee ABN is not valid'
                this.errorFields.abn = true
              } else if (response.data.errors.name !== undefined) {
                this.errorStatus = response.data.errors.name[0]
                this.errorFields.name = true
              } else {
                this.errors = 'server validation error'
              }
            }
            this.scrollToError()
          }
        })
        .catch((error) => {
          this.processing = false
          // eslint-disable-next-line
          console.log(error)
        })
    },
    findZone() {
      let coincidenceZones = []
      if (this.committee?.city) {
        coincidenceZones = this.zoneLocations.filter(location => this.committee?.city.toLowerCase() === location.suburb.toLowerCase())
      }
      if (this.committee?.post_code) {
        // eslint-disable-next-line
        coincidenceZones = this.zoneLocations.filter((location) => {
          return this.committee?.post_code === location.postcode && this.committee?.city.toLowerCase() === location.suburb.toLowerCase()
        })
      }
      if (coincidenceZones.length === 1) {
        this.zoneNotFound = false
        this.zoneFound = true
        this.committee.zone_id = coincidenceZones[0].zone_id
      } else if (coincidenceZones.length > 1) {
        this.zoneNotSpecified = true
        this.zoneFound = true
        this.zoneNotFound = false
      } else {
        this.zoneNotFound = true
        this.zoneFound = false
        this.zoneNotSpecified = false
      }
    },
    async getZoneLocations() {
      try {
        const { data: { data } } = await axios.get(`${this.$api}/zone-location/list`)
        this.zoneLocations = data || []
      } catch (error) {
        this.errorStatus = 'Can\'t fetch zone locations error'
      }
    },
    update(event) {
      event.preventDefault()

      setTimeout(() => {
        if (this.formComplete) {
          this.updateCommittee()
        }
      }, 10)
    },
    fieldsValidation(data) {
      if (data.data.status === 'validation_error' && data.data.errors.contact_phone) {
        this.errorStatus = 'contact_phone'
        this.errors = `Please enter correct mobile number`
      }
    },
    fileUpload(event) {
      this.fileError = { fileSize: false, required: false }
      this.file_name = ''
      const uploadedFile = event.target.files[0]
      this.file = uploadedFile
      if (uploadedFile) {
        this.file_name = uploadedFile.name
      }
    },
    logoUpload(event) {
      this.logoError = { logoSize: false, required: false }
      this.logo_name = ''
      const uploadedFile = event.target.files[0]
      this.logo_file = uploadedFile
      if (uploadedFile) {
        this.logo_name = uploadedFile.name
      }
      if (!isValidLogoSize(uploadedFile)) {
        this.submitStatus = 'ERROR'
        this.logoError.fileSize = true
        delete this.logo_file
        this.logo_file = undefined
        this.logo_name = ''
      }
    },
    deleteLogoAttachment() {
      delete this.logo_file
      this.logo_file = undefined
      this.logo_name = ''
    },
    deleteAttachment() {
      delete this.file
      this.file = undefined
      this.file_name = ''
    },
    deleteExistsAttachment() {
      this.committee.logo_file_path = ''
      this.committee.logo_file_name = ''
    },
    downloadLogoFile(id) {
      if (id) {
        axios.get(`${this.$api}/admin/committees/download/${id}`).then((response) => {
          window.open(response.data.path, '_blank')
        })
      }
    },
    validateBankBsb(event) {
      const value = event.target.value.replaceAll(/[^0-9]/g, '')
      const valueLength = value.length
      let newValue = value
      if (
        ((event.data === '-' && valueLength < 3) || event.inputType === 'insertFromPaste') ||
        ((event.data?.match(/[0-9]/) && valueLength === 3) || valueLength > 2)
      ) {
        newValue = `${value.replaceAll(/[^0-9]/g, '').slice(0, 3)}-${value.replaceAll(/[^0-9]/g, '').slice(3)}`
      }
      if (
        ((event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward') && valueLength === 3) ||
        valueLength < 3
      ) {
        newValue = value.replaceAll(/[^0-9]/g, '')
      }

      this.committee.bsb = newValue
    },
    setDefaultValues() {
      if (!this.committee.id) {
        this.committee = {
          notes: null,
          name: null,
          contact_email: null,
          contact_phone: null,
          contact_name: null,
          is_financial: 0,
          zone_id: null
        }
      }
    }
  }
}
</script>




// WEBPACK FOOTER //
// src/views/Committees/Profile/CommitteeProfileDetails.vue